import './style.modules.scss'

import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo } from 'react'
import xss from 'xss'

import { Link } from '../../types/Link.type'
import { LoginBannerBackgroundColor } from './LoginBannerColor.type'

export type LoginBannerProps = {
  color?: LoginBannerBackgroundColor
  facet?: boolean
  heading?: string
  description?: string
  button?: Link
  disclaimer?: string // DEPRECATED
  logoImageUrl?: string // DEPRECATED
  analyticsName?: string
  psnAnalytics?: string
  testId?: string
}

export const LoginBanner: React.FC<LoginBannerProps> = (
  props: LoginBannerProps
) => {
  const {
    color,
    facet,
    heading,
    description,
    button,
    disclaimer,
    logoImageUrl,
    analyticsName,
    psnAnalytics,
    testId
  } = props

  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('LoginBanner')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )

  if (heading === undefined && description === undefined) {
    return null
  }

  let isContentReversed: boolean
  let isButtonReversed: boolean

  const classNames = []
  switch (color) {
    case BackgroundColor.MIDNIGHT_BLUE:
      classNames.push('u-bgColorMidnightBlue')
      isContentReversed = true
      isButtonReversed = true
      break
    case BackgroundColor.PURPLE:
      classNames.push('u-bgColorPurple')
      isContentReversed = true
      isButtonReversed = true
      break
    case BackgroundColor.DARK_MINT:
      classNames.push('u-bgColorDarkMint')
      isContentReversed = true
      isButtonReversed = true
      break
    case BackgroundColor.MINT:
      if (facet) {
        isContentReversed = true
      } else {
        isContentReversed = false
      }
      isButtonReversed = true
      classNames.push('u-bgColorMint')
      break
    case BackgroundColor.LIGHT_BLUE:
      classNames.push('u-bgColorLightBlue')
      if (facet) {
        isContentReversed = true
      } else {
        isContentReversed = false
      }
      isButtonReversed = true
      break
    case BackgroundColor.LIGHT_LIME:
      classNames.push('u-bgColorLimeLight20')
      isContentReversed = false
      isButtonReversed = false
      break
    case BackgroundColor.LIGHT_MINT:
      classNames.push('u-bgColorMintLight10')
      isContentReversed = false
      isButtonReversed = false
      break
    case BackgroundColor.LIGHT_PURPLE:
      classNames.push('u-bgColorPurpleLight10')
      isContentReversed = false
      isButtonReversed = false
      break
    default:
      classNames.push('u-bgColorStone')
      isContentReversed = false
      isButtonReversed = false
      break
  }

  return (
    <div
      className={`LoginBanner ${classNames.join(
        ' '
      )} u-padding32 u-sm-padding20`}
      style={{
        ...(facet && {
          backgroundImage: `url(${window.AMPCOMPATH}images/facets/loginbanner-facets-${color}.png)`
        })
      }}
      data-testid={testId ? testId : null}
      {...atb.dataAnalyticAttribute()}
    >
      <div className={`LoginBanner-container`}>
        <div
          className={`LoginBanner-content Content Content--large ${
            isContentReversed ? 'Content--reversed' : ''
          }`}
        >
          {heading && (
            <h1
              className="u-paddingBottom24 u-textSizeMsrp4 "
              dangerouslySetInnerHTML={{ __html: xss(heading) }}
              data-testid={testId ? `${testId}-heading` : null}
            />
          )}
          <div className={`u-paddingBottom48 u-sm-paddingBottom20 `}>
            {description && (
              <div
                dangerouslySetInnerHTML={{ __html: xss(description) }}
                data-testid={testId ? `${testId}-description` : null}
              />
            )}
          </div>
        </div>
        {button && button.name && button.url && (
          <a
            className={`LoginBanner-button Button Button--primary ${
              isButtonReversed ? 'Button--reversed' : ''
            }`}
            href={button?.url}
            target={button?.target}
            data-testid={testId ? `${testId}-button` : null}
            {...atb.dataAnalyticAttribute('button')}
          >
            {button?.name}
          </a>
        )}
        {disclaimer && (
          <div
            className="no-overlay-parawidth u-marginBottom4 disclaimer-text"
            dangerouslySetInnerHTML={{ __html: xss(disclaimer) }}
          />
        )}
        {logoImageUrl && (
          <div className="graphic-logo u-posAbsolute u-overflowHidden">
            <img src={logoImageUrl} alt="" />
          </div>
        )}
      </div>
    </div>
  )
}
